import React, { useState, useEffect } from 'react';
import { X, Send, Mail } from 'lucide-react';
import { toast } from 'react-toastify';
import { templatesAPI, bulkEmailAPI } from '../../services/api';
import { resolveVariables } from './templateUtils';

const EmailTemplatePanel = ({ isOpen, onClose, module, context, recipient }) => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);

  const organizationId = localStorage.getItem('organizationId');

  useEffect(() => {
    if (!isOpen) return;
    fetchTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, module]);

  const fetchTemplates = async () => {
    setLoadingTemplates(true);
    try {
      const res = await templatesAPI.getTemplates(organizationId, 'EMAIL', module);
      setTemplates(res.data || []);
    } catch (err) {
      console.error('Failed to load email templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  const handleTemplateSelect = (e) => {
    const id = e.target.value;
    if (!id) {
      setSelectedTemplate(null);
      setSubject('');
      setBody('');
      return;
    }
    const tpl = templates.find((t) => t.id === id);
    if (!tpl) return;
    setSelectedTemplate(tpl);
    const resolved = resolveVariables(tpl, context || {});
    setSubject(resolved.subject || '');
    setBody(resolved.body || '');
  };

  const handleSend = async () => {
    if (!subject.trim()) { toast.error('Subject is required'); return; }
    if (!body.trim()) { toast.error('Email body is required'); return; }
    if (!recipient?.email) { toast.error('Recipient email not found'); return; }

    setSending(true);
    try {
      await bulkEmailAPI.sendDirect({
        email: recipient.email,
        name: recipient.name || '',
        subject,
        body,
      });
      toast.success(`Email sent to ${recipient.name || recipient.email}`);
      handleClose();
    } catch (err) {
      toast.error('Failed to send email: ' + (err.response?.data?.message || err.message));
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setSelectedTemplate(null);
    setSubject('');
    setBody('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={handleClose} />
      <div className="relative bg-white w-full max-w-lg h-full flex flex-col shadow-2xl">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b border-slate-100 flex items-center justify-between flex-shrink-0">
          <div>
            <p className="text-xs font-semibold text-blue-600 uppercase tracking-wider mb-0.5">
              Email Template
            </p>
            <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Mail size={16} className="text-slate-400" />
              {recipient?.name ? `Send Email to ${recipient.name}` : 'Send Email'}
            </h2>
            {recipient?.email && (
              <p className="text-xs text-slate-400 mt-0.5">{recipient.email}</p>
            )}
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
          {/* Template selector */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Select Template
            </label>
            <select
              onChange={handleTemplateSelect}
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
              defaultValue=""
            >
              <option value="">Write from scratch</option>
              {loadingTemplates ? (
                <option disabled>Loading templates…</option>
              ) : (
                templates.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))
              )}
            </select>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Subject <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Enter email subject…"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          {/* Body */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
              Message <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={10}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Write your email message here…"
              className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 flex-shrink-0">
          <button
            onClick={handleClose}
            disabled={sending}
            className="flex-1 px-4 py-2.5 border border-slate-200 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="flex-1 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60 transition-colors"
          >
            {sending ? (
              <>
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Sending…
              </>
            ) : (
              <>
                <Send size={14} />
                Send Email
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailTemplatePanel;
